import { useCallback, useEffect, useRef, useState } from "react";

const STATUS_LABELS = {
  unconfirmed:"未確認",
  amount_confirmed:"金額確定",
  no_charge:"今月請求なし",
};

export const billingCheckYearMonthInJapan = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"Asia/Tokyo", year:"numeric", month:"2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part=>[part.type, part.value]));
  return `${values.year}-${values.month}`;
};

const money = value => `${Number(value || 0).toLocaleString("ja-JP")}円`;
const signedMoney = value => `${Number(value || 0) > 0 ? "+" : ""}${Number(value || 0).toLocaleString("ja-JP")}円`;
const dateTime = value => value
  ? String(value).replace("-", "/").replace("-", "/").slice(0, 16)
  : "—";

const filterGridStyle = {
  boxSizing:"border-box",
  display:"grid",
  gap:9,
  gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,220px),1fr))",
  marginTop:14,
  maxWidth:"100%",
  minWidth:0,
  width:"100%",
};

const filterLabelStyle = {
  boxSizing:"border-box",
  color:"#475569",
  display:"grid",
  fontSize:12,
  gap:6,
  maxWidth:"100%",
  minWidth:0,
  width:"100%",
};

const primaryFilterControlStyle = {
  background:"#fff",
  border:"1px solid #cbd5e1",
  borderRadius:7,
  boxSizing:"border-box",
  display:"block",
  fontFamily:"inherit",
  fontSize:18,
  fontWeight:800,
  lineHeight:1.25,
  maxWidth:"100%",
  minHeight:48,
  minWidth:0,
  padding:"10px 12px",
  width:"100%",
};

const monthFilterFrameStyle = {
  alignItems:"center",
  background:"#fff",
  border:"1px solid #cbd5e1",
  borderRadius:7,
  boxSizing:"border-box",
  display:"flex",
  maxWidth:"100%",
  minHeight:48,
  minWidth:0,
  padding:"10px 12px",
  width:"100%",
};

const monthFilterInputStyle = {
  background:"transparent",
  border:0,
  borderRadius:0,
  boxSizing:"border-box",
  display:"block",
  fontFamily:"inherit",
  fontSize:18,
  fontWeight:800,
  lineHeight:1.25,
  margin:0,
  maxWidth:"100%",
  minWidth:0,
  padding:0,
  width:"100%",
};

const monthInputWebKitStyles = `
  .billing-check-month-input::-webkit-date-and-time-value,
  .billing-check-month-input::-webkit-datetime-edit {
    min-width: 0;
    padding: 0;
    text-align: left;
  }
`;

const buildingFilterControlStyle = {
  background:"#fff",
  border:"1px solid #cbd5e1",
  borderRadius:7,
  boxSizing:"border-box",
  display:"block",
  fontWeight:800,
  maxWidth:"100%",
  minHeight:44,
  minWidth:0,
  padding:"8px 10px",
  width:"100%",
};

const badgeStyle = (check) => {
  if (check.stale) return { color:"#b91c1c", background:"#fee2e2" };
  if (check.status === "amount_confirmed") return { color:"#166534", background:"#dcfce7" };
  if (check.status === "no_charge") return { color:"#1d4ed8", background:"#dbeafe" };
  return { color:"#92400e", background:"#fef3c7" };
};

export default function BillingMonthUserChecks({ store, request, access = null, onReviewChanges = null, initialYearMonth = "" }) {
  const [ym, setYm] = useState(()=>initialYearMonth || billingCheckYearMonthInJapan());
  const [facilities, setFacilities] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  const [facilityError, setFacilityError] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [pendingAmountConfirmation, setPendingAmountConfirmation] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const loadSequenceRef = useRef(0);

  useEffect(() => {
    let active = true;
    loadSequenceRef.current += 1;
    setFacilitiesLoading(true);
    setFacilityError("");
    setFacilities([]);
    setSelectedStoreId("");
    setSelectedBuildingId("");
    setSummary(null);
    if (Array.isArray(access?.facilities)) {
      const allowed = access.facilities;
      setFacilities(allowed);
      const initial = allowed.find(facility=>facility.id === store.id) || allowed[0];
      setSelectedStoreId(initial?.id || "");
      setFacilitiesLoading(false);
      return () => { active = false; };
    }
    request("/billing_month_user_checks.php?action=targets")
      .then(data=>{
        if (!active) return;
        const allowed = Array.isArray(data?.facilities) ? data.facilities : [];
        setFacilities(allowed);
        const initial = allowed.find(facility=>facility.id === store.id) || allowed[0];
        setSelectedStoreId(initial?.id || "");
      })
      .catch(requestError=>{
        if (!active) return;
        setFacilityError(requestError.message || "施設一覧の取得に失敗しました");
      })
      .finally(()=>{ if (active) setFacilitiesLoading(false); });
    return () => { active = false; };
  }, [access, request, store.id]);

  const selectedStore = facilities.find(facility=>facility.id === selectedStoreId) || null;
  const canEdit = Boolean(selectedStore?.capabilities?.can_edit);
  const canConfirm = Boolean(selectedStore?.capabilities?.can_confirm);
  const themeColor = selectedStoreId === store.id ? store.color : "#334155";

  const load = useCallback(async ({ sync = true } = {}) => {
    if (!selectedStoreId) return;
    const sequence = ++loadSequenceRef.current;
    setLoading(true);
    setSummary(null);
    setError("");
    try {
      const buildingQuery = selectedBuildingId ? `&buildingId=${encodeURIComponent(selectedBuildingId)}` : "";
      const path = `/billing_month_user_checks.php?storeId=${encodeURIComponent(selectedStoreId)}&ym=${encodeURIComponent(ym)}${buildingQuery}`;
      const current = await request(path);
      if (sequence !== loadSequenceRef.current) return;
      setSummary(current);
      if (sync && canEdit && current.billing_month?.status === "open") {
        try {
          const synced = await request("/billing_month_user_checks.php", "POST", {
            storeId:selectedStoreId,
            ym,
            ...(selectedBuildingId ? { buildingId:selectedBuildingId } : {}),
          });
          if (sequence !== loadSequenceRef.current) return;
          setSummary(synced);
        } catch (syncError) {
          if (sequence !== loadSequenceRef.current) return;
          setError(syncError.message || "対象利用者の同期に失敗しました");
        }
      }
    } catch (requestError) {
      if (sequence !== loadSequenceRef.current) return;
      setSummary(null);
      setError(requestError.message || "実費請求チェックの取得に失敗しました");
    } finally {
      if (sequence === loadSequenceRef.current) setLoading(false);
    }
  }, [selectedStoreId, selectedBuildingId, ym, request, canEdit]);

  useEffect(() => {
    let active = true;
    const run = async () => { if (active && selectedStoreId) await load(); };
    run();
    return () => { active = false; };
  }, [load, selectedStoreId]);

  const changeFacility = event => {
    loadSequenceRef.current += 1;
    setSelectedStoreId(event.target.value);
    setSelectedBuildingId("");
    setSummary(null);
    setLoading(true);
    setError("");
    setMessage("");
  };

  const changeMonth = event => {
    loadSequenceRef.current += 1;
    setYm(event.target.value);
    setSelectedBuildingId("");
    setSummary(null);
    setLoading(true);
    setError("");
    setMessage("");
  };

  const changeBuilding = event => {
    loadSequenceRef.current += 1;
    setSelectedBuildingId(event.target.value);
    setSummary(null);
    setLoading(true);
    setError("");
    setMessage("");
  };

  const checks = summary?.checks || [];
  const billingProhibited = Boolean(summary?.billing_policy?.billing_prohibited);
  const counts = summary?.counts || {};
  const storeCounts = summary?.store_counts || counts;
  const buildings = summary?.buildings || [];
  const selectedBuilding = buildings.find(building=>building.id === selectedBuildingId) || null;
  const validConfirmed = Number(counts.amount_confirmed || 0) + Number(counts.no_charge || 0) - Number(counts.stale || 0);
  const total = Number(counts.target || 0);
  const missing = Number(counts.missing || 0);
  const unconfirmed = Number(counts.unconfirmed || 0) + missing;
  const stale = Number(counts.stale || 0);
  const complete = total > 0 && missing === 0 && validConfirmed === total && unconfirmed === 0 && stale === 0;
  const storeTotal = Number(storeCounts.target || 0);
  const storeMissing = Number(storeCounts.missing || 0);
  const storeUnconfirmed = Number(storeCounts.unconfirmed || 0) + storeMissing;
  const storeStale = Number(storeCounts.stale || 0);
  const storeConfirmed = Number(storeCounts.amount_confirmed || 0) + Number(storeCounts.no_charge || 0) - storeStale;
  const storeComplete = storeTotal > 0 && storeMissing === 0 && storeConfirmed === storeTotal && storeUnconfirmed === 0 && storeStale === 0;

  const confirmCheck = async (check, status) => {
    if (!canConfirm) return;
    if (status === "no_charge") {
      const accepted = window.confirm(
        `${check.service_user_name}さんについて、今月の実費請求がないことを確認します。`,
      );
      if (!accepted) return;
    }
    setSavingId(check.id);
    setError("");
    setMessage("");
    try {
      await request("/billing_month_user_checks.php", "PUT", {
        id:check.id,
        expectedRevision:check.revision,
        status,
      });
      await load({ sync:false });
      setMessage(`${check.service_user_name}さんの確認を保存しました。`);
    } catch (requestError) {
      if (requestError.status === 409) {
        await load({ sync:false });
        setError("他の担当者によって内容が更新されました。最新状態を読み込み直してください");
      } else {
        setError(requestError.message || "確認の保存に失敗しました");
      }
    } finally {
      setSavingId(null);
    }
  };

  const confirmPendingAmount = async () => {
    const check = pendingAmountConfirmation;
    if (!check) return;
    setPendingAmountConfirmation(null);
    await confirmCheck(check, "amount_confirmed");
  };

  return <section aria-label="実費請求チェック" style={{ background:"#f8fafc", border:"1px solid #cbd5e1", borderRadius:12, padding:16, marginBottom:18 }}>
    <style>{monthInputWebKitStyles}</style>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, flexWrap:"wrap" }}>
      <div>
        <div style={{ fontSize:18, fontWeight:900, color:themeColor }}>実費請求チェック</div>
        <div style={{ fontSize:12, color:"#64748b", marginTop:3 }}>対象利用者ごとに、当月の実費請求を最終確認します。</div>
      </div>
      <button type="button" onClick={()=>load()} disabled={loading || facilitiesLoading || !selectedStoreId} style={{ minHeight:44, padding:"8px 13px" }}>最新状態を再読込</button>
    </div>

    <div aria-label="実費請求チェック絞り込み条件" style={filterGridStyle}>
      <label style={filterLabelStyle}>施設
        <select aria-label="実費請求チェック施設" value={selectedStoreId} onChange={changeFacility} disabled={facilitiesLoading || facilities.length === 0 || savingId !== null} style={primaryFilterControlStyle}>
          {facilities.length === 0 && <option value="">{facilitiesLoading ? "施設を確認中…" : "選択できる施設はありません"}</option>}
          {facilities.map(facility=><option key={facility.id} value={facility.id}>{facility.name}</option>)}
        </select>
      </label>
      <label style={filterLabelStyle}>対象月
        <span aria-label="実費請求チェック対象月枠" style={monthFilterFrameStyle}>
          <input className="billing-check-month-input" aria-label="実費請求チェック対象月" type="month" value={ym} onChange={changeMonth} disabled={!selectedStoreId || savingId !== null} style={monthFilterInputStyle}/>
        </span>
      </label>
      <label style={filterLabelStyle}>建物（表示絞り込み）
        <select aria-label="実費請求チェック建物" value={selectedBuildingId} onChange={changeBuilding} disabled={!selectedStoreId || loading || savingId !== null} style={buildingFilterControlStyle}>
          <option value="">すべての建物・未設定</option>
          {buildings.map(building=><option key={building.id} value={building.id}>{building.name}{building.status === "inactive" ? "（終了）" : ""}</option>)}
        </select>
      </label>
    </div>

    {facilityError && <div role="alert" style={{ marginTop:10, padding:"9px 11px", borderRadius:7, background:"#fee2e2", color:"#991b1b", fontSize:12, fontWeight:700 }}>{facilityError}</div>}
    {error && <div role="alert" style={{ marginTop:10, padding:"9px 11px", borderRadius:7, background:"#fee2e2", color:"#991b1b", fontSize:12, fontWeight:700 }}>{error}</div>}
    {message && <div role="status" style={{ marginTop:10, color:"#166534", fontSize:12, fontWeight:700 }}>{message}</div>}
    {billingProhibited && <div role="status" style={{ marginTop:10, padding:"9px 11px", border:"1px solid #f59e0b", borderRadius:7, background:"#fffbeb", color:"#92400e", fontSize:12, fontWeight:800 }}>2026年9月は練習月・請求禁止です。入力内容は閲覧できますが、利用者別の最終承認はできません。</div>}

    {facilitiesLoading || loading ? <div role="status" style={{ padding:"22px 0", color:"#64748b" }}>{facilitiesLoading ? "利用可能な施設を確認中…" : `${selectedStore?.name || "選択施設"}の読み込み中…`}</div> : facilities.length === 0 ? <div style={{ marginTop:14, padding:14, background:"#fff7ed", color:"#9a3412", borderRadius:8, fontSize:13 }}>
      実費請求チェックを閲覧できる施設はありません。
    </div> : !summary?.billing_month ? <div style={{ marginTop:14, padding:14, background:"#fff7ed", color:"#9a3412", borderRadius:8, fontSize:13 }}>
      この対象月はまだ作成されていません。上の「月次漏れ確認」で確認項目を生成後、再読込してください。
    </div> : <>
      <div style={{ marginTop:12, fontSize:11, fontWeight:800, color:"#475569" }}>{selectedStore?.name || "施設"}全体</div>
      <div aria-label="実費請求チェック集計" style={{ display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:4, marginTop:4 }}>
        {[["対象利用者", storeTotal], ["確認済み", storeConfirmed], ["未確認", storeUnconfirmed], ["再確認必要", storeStale]].map(([label,value])=><div key={label} aria-label={`${label}${value}名`} style={{ minWidth:0, textAlign:"center", background:"#fff", border:"1px solid #e2e8f0", borderRadius:7, padding:"6px 2px 5px" }}>
          <div style={{ fontSize:9, lineHeight:1.25, color:"#64748b", whiteSpace:"nowrap" }}>{label}</div><div style={{ fontSize:17, lineHeight:1.2, fontWeight:900, color:label.includes("未確認") || label.includes("再確認") ? "#b91c1c" : themeColor }}>{value}<span style={{ fontSize:9, marginLeft:1 }}>名</span></div>
        </div>)}
      </div>

      <div style={{ marginTop:8, padding:"8px 10px", borderRadius:8, fontSize:13, fontWeight:900, background:storeComplete ? "#dcfce7" : "#fff7ed", color:storeComplete ? "#166534" : "#9a3412" }}>
        {storeComplete ? "全利用者の確認が完了しています（施設全体）" : <span>施設全体：未確認{storeUnconfirmed}名　再確認必要{storeStale}名</span>}
      </div>
      {selectedBuilding && <>
        <div style={{ marginTop:10, fontSize:11, fontWeight:800, color:"#475569" }}>{selectedBuilding.name}の表示対象</div>
        <div aria-label="実費請求チェック建物集計" style={{ display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:4, marginTop:4 }}>
          {[["対象利用者", total], ["確認済み", validConfirmed], ["未確認", unconfirmed], ["再確認必要", stale]].map(([label,value])=><div key={label} aria-label={`表示建物${label}${value}名`} style={{ minWidth:0, textAlign:"center", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:7, padding:"6px 2px 5px" }}>
            <div style={{ fontSize:9, lineHeight:1.25, color:"#475569", whiteSpace:"nowrap" }}>{label}</div><div style={{ fontSize:17, lineHeight:1.2, fontWeight:900, color:label.includes("未確認") || label.includes("再確認") ? "#b91c1c" : themeColor }}>{value}<span style={{ fontSize:9, marginLeft:1 }}>名</span></div>
          </div>)}
        </div>
        <div style={{ marginTop:7, padding:"7px 9px", borderRadius:7, background:"#eff6ff", color:"#1e3a8a", fontSize:11, fontWeight:700 }}>
          {complete ? "この建物の表示対象者は確認済みです。" : `この建物：未確認${unconfirmed}名・再確認必要${stale}名。`}
          建物は表示の絞り込みです。月締めは施設全体の確認状態で判定されます。
        </div>
      </>}
      {!canEdit && Number(counts.missing || 0) > 0 && <div style={{ marginTop:7, padding:"7px 9px", borderRadius:7, background:"#f8fafc", color:"#475569", fontSize:11 }}>
        対象利用者の同期権限がないため、未同期の利用者は閲覧のみです。
      </div>}
      {!canConfirm && <div style={{ marginTop:7, padding:"7px 9px", borderRadius:7, background:"#f8fafc", color:"#475569", fontSize:11 }}>
        閲覧専用です。金額確定・今月請求なし・再確認は実行できません。
      </div>}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,290px),1fr))", gap:7, marginTop:9 }}>
        {checks.map(check=>{
          const statusStyle = badgeStyle(check);
          const confirmed = check.status !== "unconfirmed";
          const saving = savingId === check.id;
          const needsAction = check.status === "unconfirmed" || check.stale;
          return <article key={check.id} aria-label={`${check.service_user_name} 実費請求確認`} style={{ background:"#fff", border:`1px solid ${check.stale ? "#fca5a5" : "#e2e8f0"}`, borderLeft:`5px solid ${check.stale ? "#dc2626" : statusStyle.color}`, borderRadius:9, padding:"9px 10px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:15, lineHeight:1.25, fontWeight:900, overflowWrap:"anywhere" }}>{check.service_user_name}</div>
                <span style={{ ...statusStyle, display:"inline-block", marginTop:4, borderRadius:20, padding:"2px 7px", fontSize:10, lineHeight:1.35, fontWeight:900, whiteSpace:"nowrap" }}>
                  {check.stale ? "再確認必要" : STATUS_LABELS[check.status] || "未確認"}
                </span>
              </div>
              <div style={{ flex:"0 0 auto", textAlign:"right" }}>
                <div style={{ fontSize:9, color:"#64748b" }}>現在金額</div>
                <div style={{ fontSize:20, lineHeight:1.2, fontWeight:900 }}>{money(check.current_amount)}</div>
              </div>
            </div>
            {check.stale && <div style={{ marginTop:6, padding:"6px 8px", background:"#fee2e2", color:"#991b1b", borderRadius:6, fontSize:11, lineHeight:1.45, fontWeight:800 }}>
              確認後に内容が変更されています。再確認してください
            </div>}
            {check.stale && check.review_change && <div aria-label={`${check.service_user_name} 再確認差額`} style={{ display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:5, marginTop:7 }}>
              {[
                ["前回確定額",money(check.review_change.previous_amount)],
                ["現在金額",money(check.review_change.current_amount)],
                ["差額",signedMoney(check.review_change.difference)],
              ].map(([label,value])=><div key={label} style={{ minWidth:0, padding:"6px 4px", border:"1px solid #fecaca", borderRadius:6, background:"#fff7f7", textAlign:"center" }}><div style={{ fontSize:9, color:"#7f1d1d" }}>{label}</div><div style={{ marginTop:2, color:"#991b1b", fontSize:12, fontWeight:900, overflowWrap:"anywhere" }}>{value}</div></div>)}
            </div>}
            <div style={{ marginTop:6, paddingTop:6, borderTop:"1px solid #f1f5f9", fontSize:10, lineHeight:1.5, color:"#64748b", overflowWrap:"anywhere" }}>
              {check.status === "no_charge"
                ? <span>{check.stale ? "以前の確認内容" : "確認内容"} <b style={{ color:check.stale ? "#b91c1c" : "#1d4ed8" }}>今月請求なし</b>{check.stale && <span>（その後内容変更）</span>}</span>
                : <span>確認時 <b style={{ color:check.stale && confirmed && Number(check.confirmed_amount) !== Number(check.current_amount) ? "#b91c1c" : "#475569" }}>{confirmed ? money(check.confirmed_amount) : "未確認"}</b></span>}
              {confirmed && <span>　・　{check.confirmed_by_name || check.confirmed_by || "不明"}　・　{dateTime(check.confirmed_at)}</span>}
            </div>
            {needsAction && canConfirm && !billingProhibited && <div style={{ marginTop:7 }}>
              {check.stale && check.review_change && onReviewChanges ? <button type="button" disabled={saving} onClick={()=>onReviewChanges(check.review_change)} style={{ width:"100%", minHeight:44, border:"1px solid #dc2626", borderRadius:8, background:"#fff", color:"#b91c1c", fontWeight:900 }}>
                変更内容を確認
              </button> : Number(check.current_amount) > 0 ? <button type="button" disabled={saving} onClick={()=>setPendingAmountConfirmation(check)} style={{ width:"100%", minHeight:44, border:0, borderRadius:8, background:themeColor, color:"#fff", fontWeight:900 }}>
                {saving ? "保存中…" : check.stale ? `この金額で再確定（${money(check.current_amount)}）` : `この金額で確定（${money(check.current_amount)}）`}
              </button> : <button type="button" disabled={saving} onClick={()=>confirmCheck(check, "no_charge")} style={{ width:"100%", minHeight:44, border:"1px solid #93c5fd", borderRadius:8, background:"#eff6ff", color:"#1d4ed8", fontWeight:900 }}>
                {saving ? "保存中…" : check.stale ? "今月請求なしを再確認" : "今月請求なし"}
              </button>}
            </div>}
          </article>;
        })}
        {checks.length === 0 && <div style={{ padding:20, textAlign:"center", color:"#64748b" }}>対象利用者はいません。</div>}
      </div>
    </>}
    {pendingAmountConfirmation && <div role="presentation" style={{ position:"fixed", inset:0, zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16, background:"rgba(15,23,42,.58)" }}>
      <div role="dialog" aria-modal="true" aria-labelledby="billing-amount-confirmation-title" style={{ width:"100%", maxWidth:420, boxSizing:"border-box", background:"#fff", borderRadius:12, boxShadow:"0 20px 45px rgba(15,23,42,.28)", padding:18 }}>
        <div id="billing-amount-confirmation-title" style={{ fontSize:18, fontWeight:900, color:"#0f172a" }}>請求金額を確定しますか？</div>
        <div style={{ marginTop:7, color:"#475569", fontSize:12, lineHeight:1.6 }}>内容を確認してから「確定する」を押してください。</div>
        <dl style={{ margin:"14px 0 0", display:"grid", gap:8 }}>
          {[
            ["利用者", pendingAmountConfirmation.service_user_name],
            ["対象月", ym],
            ["確定金額", money(pendingAmountConfirmation.current_amount)],
          ].map(([label,value])=><div key={label} style={{ display:"grid", gridTemplateColumns:"72px minmax(0,1fr)", gap:8, padding:"9px 10px", background:"#f8fafc", borderRadius:7 }}><dt style={{ color:"#64748b", fontSize:11 }}>{label}</dt><dd style={{ margin:0, fontSize:14, fontWeight:900, overflowWrap:"anywhere" }}>{value}</dd></div>)}
        </dl>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9, marginTop:16 }}>
          <button type="button" onClick={()=>setPendingAmountConfirmation(null)} style={{ minHeight:48, border:"1px solid #cbd5e1", borderRadius:8, background:"#fff", color:"#334155", fontWeight:900 }}>戻る／キャンセル</button>
          <button type="button" onClick={confirmPendingAmount} style={{ minHeight:48, border:0, borderRadius:8, background:themeColor, color:"#fff", fontWeight:900 }}>確定する</button>
        </div>
      </div>
    </div>}
  </section>;
}
